import { useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import { RightAlignedFlex } from '../components/styles';
import { useManagedFleetsQuery } from './hooks/useManagedFleetsQuery';

type ManagedFleetType = ManagedFleetsQuery['managedFleets']['collection'][0];
type ColumnProperties = Pick<ManagedFleetType, 'id' | 'name' | 'totals'>;

const Fleets = () => {
  const { isSuperUser } = useCurrentUserContext();

  const [page, setPage] = useState(1);

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data } = useManagedFleetsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'name',
      title: t`Name`,
      DataCell: ({ rowData: { name } }) => <span>{name}</span>,
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`ID`,
      DataCell: ({ rowData: { id } }) => <span>{id}</span>,
      sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'primaryContact',
      title: t`Primary Contact`,
      DataCell: () => <span>--</span>,
      // sortBy: (direction) => onSort('PRIMARY_CONTACT', direction),
    },
    {
      name: 'depots',
      title: t`Depots`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{totals?.assetGroupings?.total}</span>
      ),
      sortBy: (direction) => onSort('DEPOTS_COUNT', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totals } }) => (
        <span>{totals?.assetGroupings?.enabledAssets}</span>
      ),
      sortBy: (direction) => onSort('ENABLED_ASSET_COUNT', direction),
    },
  ];

  const tableData = data?.managedFleets.collection ?? [];
  const pagination = data?.managedFleets.pagination;

  return (
    <>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <Button text={t`Create Fleet`} icon={Plus} size="small" />
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Fleet by Name</Trans>
          </P>
          <SearchInput name="searchByFleet" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      <Table
        data={tableData}
        columns={columns}
        onSort={onSort}
        defaultSort={defaultSort}
        footer={() => (
          <PaginationFooter
            title={t`Fleets`}
            setCurrentPage={setPage}
            totalOnPage={tableData.length}
            page={pagination?.page}
            perPage={pagination?.perPage}
            totalEntries={pagination?.totalEntries}
            totalPages={pagination?.totalPages}
          />
        )}
      />
    </>
  );
};

export default Fleets;
